import {
    ActionStatus,
    ActionStatusLookup,
    GamePlayerStatus,
    GamePlayerStatusLookup,
    TurnStatus,
    TurnStatusLookup
} from '../../Constants';
import artificialActionChooser from './ActionChooser/ActionChooser';
import artificialAdvertisementAction from './Actions/AIAdvertisement';
import artificialAntiJuice from './Actions/AIAntiJuice';
import artificialChooseFaction from './Actions/AIChooseFaction';
import artificialDraftAction from './Actions/AIDraft';
import artificialGhostAction from './Actions/AIGhostAction';
import artificialInitialLodging from './Actions/AIInitialLodging';
import artificialLodgingAction from './Actions/AILodging';
import artificialOpeningGhostPlacement from
    './Actions/AIOpeningGhostPlacement';
import artificialPlaceGhost from './Actions/AIPlaceGhost';
import artificialRandomAction from './Actions/AIRandomAction';
import artificialReceiveSpecial from './Actions/AIReceiveSpecialGuest';
import artificialRenovationAction from './Actions/AIRenovation';
import artificialResearchAction from './Actions/AIResearch';
import artificialScorePhase from './Actions/AIScorePhase';
import artificialStaffAction from './Actions/AIStaffAction';
import artificialSelectAction from './Actions/AISelectAction';
import artificialWaiting, {
    artificialActionComplete,
    artificialExecuteAction,
    artificialLocked
} from './Actions/Waiting';
import AIMessagePackage from './AIMessagePackage';
import SpeedController from './SpeedController';
import { REJECT_MESSAGE_EVENT } from '../Insights/InsightsWindow';


// CC_FEAI_REJECT_RETRY: a systematic FE<->engine mismatch must not
// ping-pong forever. After this many consecutive rejected retries with
// no accepted advance in between, the seat belays to manual controls
// instead of re-arming.
const MAX_CONSECUTIVE_REJECT_RETRIES = 3;


export const STATUS_ROUTES = {};
STATUS_ROUTES[GamePlayerStatus.PICK_PLAYER_ONE_ID] = null;
STATUS_ROUTES[GamePlayerStatus.PICK_FACTION_ID] = null;
STATUS_ROUTES[GamePlayerStatus.PICK_STATION_ID] = null;
STATUS_ROUTES[GamePlayerStatus.PICK_DRAFT_ID] = artificialDraftAction;
STATUS_ROUTES[GamePlayerStatus.GHOSTED_ID] = artificialPlaceGhost;
// 2026-05-16: GHOST_STARTER_ID is a transient backend state during
// Ghost Wave setup. Seats can get stuck there with attacking_ghosts
// > 0; treating it the same as GHOSTED_ID lets the AI place a ghost
// and unstick the chain. Backend's click_router now accepts clicks
// for this status too (routes them to message_while_ghosted).
STATUS_ROUTES[GamePlayerStatus.GHOST_STARTER_ID] = artificialPlaceGhost;
STATUS_ROUTES[GamePlayerStatus.DEFENDING_ID] = artificialAntiJuice;
STATUS_ROUTES[GamePlayerStatus.SPECIAL_ID] = artificialReceiveSpecial;  // receive special guest.
STATUS_ROUTES[GamePlayerStatus.SCORING_ID] = artificialScorePhase;
STATUS_ROUTES[GamePlayerStatus.ADVERTISING_ID] = artificialAdvertisementAction;
STATUS_ROUTES[GamePlayerStatus.RESEARCHING_ID] = artificialResearchAction;
STATUS_ROUTES[GamePlayerStatus.LODGING_ID] = artificialLodgingAction;
STATUS_ROUTES[GamePlayerStatus.ROBOTING_ID] = artificialStaffAction;
STATUS_ROUTES[GamePlayerStatus.RENOVATING_ID] = artificialRenovationAction;
STATUS_ROUTES[GamePlayerStatus.GHOSTING_ID] = artificialGhostAction;
STATUS_ROUTES[GamePlayerStatus.WAITING_ID] = artificialWaiting;
STATUS_ROUTES[GamePlayerStatus.RANDOM_ID] = artificialRandomAction;
STATUS_ROUTES[GamePlayerStatus.GAME_SETUP_ID] = artificialInitialLodging;
// Manual 3.1 opening ghost placement. The seat lands here after game
// launch (before Initial Lodging) when config['ghosts'] is on.
STATUS_ROUTES[GamePlayerStatus.OPENING_GHOST_PLACEMENT_ID] =
    artificialOpeningGhostPlacement;
// Manual §3 in-game faction ceremony. Clockwise from Player One,
// each seat commits its faction. Auto-confirm path mirrors backend
// Fossa _choose_faction handler.
STATUS_ROUTES[GamePlayerStatus.CHOOSE_FACTION_ID] = artificialChooseFaction;
// 2026-05-15: Once the game ends the seat lands in WIN/TIED/GAME_OVER.
// The AI has no decisions left — route to the same no-op as WAITING so
// the suggestion panel says something benign instead of "I am not sure
// what to do in this situation."
STATUS_ROUTES[GamePlayerStatus.WIN_ID] = artificialWaiting;
STATUS_ROUTES[GamePlayerStatus.GAME_OVER_ID] = artificialWaiting;
STATUS_ROUTES[GamePlayerStatus.TIED_ID] = artificialWaiting;


const TURN_STATUS_ROUTES = {};
TURN_STATUS_ROUTES[TurnStatus.ACTION_COMPLETE_ID] = artificialActionComplete;
TURN_STATUS_ROUTES[TurnStatus.CHOOSE_ACTIONS_ID] = artificialActionChooser;
TURN_STATUS_ROUTES[TurnStatus.SELECT_ACTION_ID] = artificialSelectAction;
TURN_STATUS_ROUTES[TurnStatus.EXECUTE_ACTION_ID] = artificialExecuteAction;
TURN_STATUS_ROUTES[TurnStatus.LOCKED_ID] = artificialLocked;


// CC_RESTORE_OWNER_ONLY_AI_DRIVER (2026-06-04): the
// TERMINAL_DECISION_STATUSES set + its empowered-gate bypass that lived
// here (CC_AI_REFERRAL_STALL_HARDEN, 2026-06-02) are gone. The owner
// (group leader) is now the SOLE FE-AI driver for every status. The
// game-531 deadlock the bypass guarded against is handled structurally
// instead — action-chain enforcement rejects stale duplicate clicks at
// the protocol layer (engine/clicks/click_router._check_action_chain)
// and the reconnect path re-engages the owner's AI on return. See
// shouldAIUpdate below.


function aiRouter(gamePlayerController, aiPackage) {
    let statusId = gamePlayerController.gamePlayerData.status;
    if (statusId in STATUS_ROUTES) {
        const route = STATUS_ROUTES[statusId];
        if (route !== null) {
            return route(gamePlayerController, aiPackage);
        }
    }
    let turnStatus = gamePlayerController.gamePlayerData.turn_status;
    if (turnStatus in TURN_STATUS_ROUTES) {
        const route = TURN_STATUS_ROUTES[turnStatus];
        if (route !== null) {
            return route(gamePlayerController, aiPackage);
        }
    }
    aiPackage.addToMessage('I am not sure what to do in this situation.');
}


export class ArtificialPlayer {
    constructor(gamePlayerController) {
        this.gamePlayerController = gamePlayerController;
        this.seat = gamePlayerController.seat;
        const gamePlayerData = this.gamePlayerController.gamePlayerData;
        this.memory = {
            name: 'Default AI',
            actions: [],
            currentPrimaryAction: null,
        };
        this.currentAIPackage = null;
        this.lastAIPackage = null;
        this.lastAIPackageExecution = null;
        this.speedController = new SpeedController(this);
        this.previousStatus = {
            gamePlayerStatus: gamePlayerData.status,
            turnStatus: gamePlayerData.turn_status,
            actionStatus: gamePlayerData.action_status,
            robotAssignments: gamePlayerData.robot_assignments,
            station: gamePlayerData.station,
            docks: gamePlayerData.docks,
            currentPlayerId: null,
        }
        this.modified = performance.now();

        this.autoExecute = null;

        // CC_FEAI_REJECT_RETRY: reject-armed one-shot retry state. A typed
        // engine Reject for a seat this client drives sets retryArmed; the
        // next GamePlayer patch for the seat consumes it and fires the AI
        // even though no hasStatusChanged-tracked field moved (the rearm
        // patch only rotates last_action_uuid). consecutiveRejectRetries
        // breaks a systematic reject<->retry loop.
        this.retryArmed = false;
        this.consecutiveRejectRetries = 0;

        window.addEventListener(
            this.gamePlayerController.eventName,
            this.updateEvent);
        window.addEventListener(
            'group-update-event',
            this.groupUpdateEvent);
        // 2026-05-14: Phase 0.8 Initial Lodging Round advances
        // game.current_player without touching the new active
        // seat's GamePlayer row, so its game-player-N-event never
        // fires. Listen for the game-level event too so the AI
        // for the seat that just became current_player gets a
        // chance to act on its turn.
        window.addEventListener(
            'game-event',
            this.gameUpdateEvent);
        // CC_FEAI_REJECT_RETRY: listen for typed engine Rejects so a
        // rejected Feai click can re-fire on the rearm patch. Added the
        // same way as the three listeners above; like them it is never
        // removed (this class has no teardown — flagged in the hand-back).
        window.addEventListener(
            REJECT_MESSAGE_EVENT,
            this.rejectEvent);
    }

    groupUpdateEvent = () => {
        if (!this.speedController.isEngaged()) {
            this.updateEvent();
        }
    }

    gameUpdateEvent = () => {
        // 2026-05-15: Narrowed from "fire on every game-event" to
        // "fire only during the Initial Lodging Round." The broad
        // listener was double-firing the AI during normal turns:
        // once during the recursive game-event dispatch (with stale
        // per-seat data) and again when the matching
        // game-player-event arrived. Each fire sent its own Click,
        // and the second one was the wrong shape for the new state
        // — hence the "Advertisement / place" arriving during
        // SELECT_ACTION and the "ActionGrid" arriving during
        // ADVERTISING errors. During normal turn-taking the
        // game-player-event drives the AI just fine; only the
        // ceremonies whose clockwise advance mutates ONLY
        // game.current_player (not any per-seat row) need this
        // trigger.
        // 2026-05-18: Manual §3 in-game faction ceremony has the
        // same shape as Initial Lodging — _advance_or_finish in
        // engine/round_choices/choose_faction.py saves game with a
        // new current_player but doesn't touch the next picker's
        // GamePlayer row, so its game-player-N-event never fires
        // (GamePlayerController.updateGamePlayerData short-circuits
        // on the JSON.stringify diff). Without this extra gate the
        // AI for seats 1..5 just sits in status 33 indefinitely.
        const status =
            this.gamePlayerController.gamePlayerData.status;
        if (status !== GamePlayerStatus.GAME_SETUP_ID
                && status !== GamePlayerStatus.CHOOSE_FACTION_ID) {
            return;
        }
        if (!this.speedController.isEngaged()) {
            this.updateEvent();
        }
    }

    updateEvent = () => {
        if (this.autoExecute === null &&
            this.gamePlayerController.gamePlayerData.artificial_player > 2) this.autoExecute = true;
        // CC_RECRUIT_AI_PERSIST: a seat taken over by a human mid-game
        // (recruit / Request-a-Seat) broadcasts a per-seat GamePlayer patch
        // with artificial_player cleared. The sticky autoExecute that made
        // THIS client self-play the AI seat would otherwise persist (it is
        // set once and never reset above), so the owner's driver keeps
        // playing a seat that is now a human's. Relinquish to advisor-only
        // the moment artificial_player goes null: shouldAIUpdate already
        // takes the human (=== null) branch, but only clearing autoExecute
        // stops the concur() self-play on the seat's next status change.
        if (this.gamePlayerController.gamePlayerData.artificial_player === null
                && this.autoExecute) {
            this.autoExecute = false;
            this.retryArmed = false;
        }
        const advanced = this.shouldAIUpdate();
        if (advanced) {
            // Genuine state advance: the prior click was accepted-
            // equivalent, so the consecutive-reject streak is broken
            // (CC_FEAI_REJECT_RETRY).
            this.consecutiveRejectRetries = 0;
        }
        // A rejected Feai click rotates last_action_uuid via rearm() but
        // moves no hasStatusChanged-tracked field, so shouldAIUpdate() is
        // false on the rearm patch. The reject-armed one-shot fires the AI
        // anyway, on that patch, so the seat isn't stranded
        // (CC_FEAI_REJECT_RETRY). The owner-only gate was enforced when the
        // flag was armed in rejectEvent.
        const retry = this.retryArmed && !advanced;
        if (retry) {
            this.retryArmed = false;
        }
        if (advanced || retry) {
            console.log('AI UPDATE EVENT', this.seat);
            this.previousStatus = this.getStatus();
            this.queryTheNextThing();
            if (this.autoExecute) {
                // CC_AUTO_DRIVER_SINGLE_EXECUTE: an autoExecute seat
                // self-plays. queryTheNextThing only built the suggestion, so
                // the single execution happens here, exactly once, through
                // the canonical concur path. stopCountdown() cancels any
                // pending engaged countdown so it cannot deliver a second
                // concur for this patch — the eager-execute + engaged-concur
                // stacking was the double fire. Feai seats are never
                // hand-engaged (the speed UI binds only to the seated
                // controller), so concur is driven directly here rather than
                // through the countdown, preserving immediate self-play.
                this.speedController.stopCountdown();
                this.concur();
            } else {
                // Human-advised seat: show the suggestion and let the
                // SpeedController concur — after the countdown when engaged,
                // or on the human's explicit Concur in manual mode.
                this.speedController.waitToConcur();
            }
            this.notifyOfUpdate();
        }
    }

    rejectEvent = (event) => {
        const detail = event && event.detail;
        const rejectedId = this._extractRejectGamePlayerId(detail);
        if (rejectedId === null) {
            return;
        }
        const gamePlayerData = this.gamePlayerController.gamePlayerData;
        // Each seat's ArtificialPlayer hears every Reject; only the one
        // whose seat the Reject targets reacts.
        if (Number(rejectedId) !== Number(gamePlayerData.id)) {
            return;
        }
        // Only a seat THIS client is auto-driving re-fires on reject. Two
        // drive modes qualify: (a) a Feai seat this client owns — mirror the
        // shouldAIUpdate owner-only gate (isFeai && empowered); (b) the
        // own-seat auto this client self-plays — this.autoExecute, the
        // sticky flag updateEvent sets for a seat it drives via concur
        // (CC_AUTO_DRIVER_SINGLE_EXECUTE). Without (b) an own-seat-auto
        // reject gets the rearm patch but never re-fires: "auto" silently
        // pauses until the human notices and clicks (CC_AUTO_SEAT_REJECT_RETRY).
        // A hand-played human seat (artificial_player === null → autoExecute
        // never set) stays un-armed — its rejected click is the human's to
        // retry, and InsightsWindow renders the correction.
        const empowered =
            this.gamePlayerController.game.groupHandler.isGroupLeader();
        const isFeai = gamePlayerData.artificial_player
            && gamePlayerData.artificial_player > 2;
        if (!((isFeai && empowered) || this.autoExecute)) {
            return;
        }
        this.consecutiveRejectRetries += 1;
        if (this.consecutiveRejectRetries > MAX_CONSECUTIVE_REJECT_RETRIES) {
            // Loop-breaker: a systematic FE<->engine mismatch must not
            // ping-pong forever. Surface manual controls instead of
            // re-arming.
            console.warn(
                `s${this.seat} - reject retry cap reached `
                + `(${this.consecutiveRejectRetries}); belaying.`);
            this.belay();
            return;
        }
        this.retryArmed = true;
    };

    _extractRejectGamePlayerId = (detail) => {
        if (!detail) {
            return null;
        }
        // The Reject arrives as the full OutgoingMessage wrapper
        // ({type, game, datetime, package: <RejectWire>}), so the
        // RejectWire's game_player id lives at detail.package.game_player.
        // A flat RejectWire (the shape InsightsWindow + its tests assume)
        // carries it at detail.game_player. Read nested first, fall back to
        // flat. See the hand-back note on the InsightsWindow shape.
        const wire = (detail.package && typeof detail.package === 'object')
            ? detail.package : detail;
        const id = wire.game_player;
        return (id === undefined || id === null) ? null : id;
    };

    notifyOfUpdate = () => {
        window.dispatchEvent(new Event('artificial-player-update-event'));
    }

    getStatus = () => {
        const gamePlayerData = this.gamePlayerController.gamePlayerData;
        // current_player is a game-level FK to GamePlayer.id; we
        // track it here so seat transitions during the Initial
        // Lodging Round (and any other turn-by-turn flow that
        // doesn't mutate the per-seat row) still trip
        // hasStatusChanged and let the AI react.
        const game = this.gamePlayerController.game;
        const gameData = (game && game.gameData) || null;
        return {
            gamePlayerStatus: gamePlayerData.status,
            turnStatus: gamePlayerData.turn_status,
            actionStatus: gamePlayerData.action_status,
            robotAssignments: gamePlayerData.robot_assignments,
            station: gamePlayerData.station,
            docks: gamePlayerData.docks,
            lodging: gamePlayerData.lodging,
            currentPlayerId: gameData ? gameData.current_player : null,
        };
    }

    hasStatusChanged = () => {
        const newStatus = this.getStatus();
        let changed = false;
        for (let [key, value] of Object.entries(newStatus)) {
            if (JSON.stringify(this.previousStatus[key]) !== JSON.stringify(value)) {
                changed = true;
                let displayValue = value;
                let displayPrevious = this.previousStatus[key];
                if (key === 'actionStatus') {
                    displayValue = ActionStatusLookup[displayValue];
                    displayPrevious = ActionStatusLookup[displayPrevious];
                }
                if (key === 'turnStatus') {
                    displayValue = TurnStatusLookup[displayValue];
                    displayPrevious = TurnStatusLookup[displayPrevious];
                }
                if (key === 'gamePlayerStatus') {
                    displayValue = GamePlayerStatusLookup[displayValue];
                    displayPrevious = GamePlayerStatusLookup[displayPrevious];
                }
                console.log(`s${this.seat} - ${key} changed from ${displayPrevious} to ${displayValue}`)
            }
        }
        return changed;
    }

    shouldAIUpdate = () => {
        const empowered = this.gamePlayerController.game.groupHandler.isGroupLeader();
        const gamePlayerData = this.gamePlayerController.gamePlayerData;
        // Human seat (no AI persona): unchanged legacy behavior.
        if (gamePlayerData.artificial_player === null) {
            return this.hasStatusChanged();
        }
        // Frontend-AI seat (Feai, id > 2). Backend-AI seats (1, 2) fall
        // through to undefined — the backend drives them.
        // CC_RESTORE_OWNER_ONLY_AI_DRIVER (2026-06-04): the owner (group
        // leader) is the SOLE driver of the FE AI, for EVERY status —
        // terminal-decision ones included. The earlier
        // TERMINAL_DECISION_STATUSES bypass (CC_AI_REFERRAL_STALL_HARDEN,
        // 2026-06-02) let every connected client drive terminal-decision
        // clicks to dodge the game-531 deadlock; that overcorrected into
        // the duplicate-click pattern that drove the G45 chain. The
        // deadlock is now handled structurally, not by fanning clicks
        // out: the action-chain enforcement in
        // engine/clicks/click_router._check_action_chain rejects stale
        // duplicate clicks at the protocol layer (only the chain-head
        // driver advances), and the reconnect path (SocketHandler
        // ws-open-event → group rendezvous → fresh state push) re-engages
        // the owner's AI when the owner returns mid-decision.
        // CC_FEAI_REJECT_RETRY (2026-06-04): the structural claim above
        // covered DETECTION but not RECOVERY. When the engine rejects a
        // Feai click it rearms (rotates last_action_uuid, re-broadcasts)
        // but only re-drives BACKEND AIs; the FE AI never re-fires because
        // the rearm patch changes no hasStatusChanged-tracked field. Game
        // 46 (faction-ceremony deadlock) stalled on exactly this. Recovery
        // now lives in this class: a Reject for a seat this client drives
        // arms a one-shot retry (rejectEvent) that fires on the next
        // GamePlayer patch — the rearm patch, which carries the fresh chain
        // head. Without it a single reject strands the seat.
        if (gamePlayerData.artificial_player && gamePlayerData.artificial_player > 2) {
            if (empowered) {
                return this.hasStatusChanged();
            }
        }
    }


    setMemory = (key, value) => {
        this.memory[key] = value;
    };

    _buildNewAIPackage = (execute = false) => {
        this.currentAIPackage = new AIMessagePackage(
            this.gamePlayerController, this, execute);
        this.currentAIPackage.name = this.memory.name;
    };

    queryTheNextThing = () => {
        // CC_AUTO_DRIVER_SINGLE_EXECUTE: suggestion-only in EVERY mode now.
        // The eager `if (autoExecute) return doTheNextThing()` branch that
        // used to live here was a second execution path: paired with the
        // engaged SpeedController concur in updateEvent it ran the action
        // twice on the same patch. Execution flows solely through concur —
        // every route in Actions/ gates its click emission on
        // aiPackage.execute, so this suggest run is side-effect-free.
        this.lastAIPackage = this.currentAIPackage;
        this._buildNewAIPackage();
        aiRouter(this.gamePlayerController, this.currentAIPackage);
        this.modified = performance.now();
        this.currentAIPackage.done();
    };

    doTheNextThing = () => {
        this._buildNewAIPackage(true);
        aiRouter(this.gamePlayerController, this.currentAIPackage);
        this.modified = performance.now();
        this.currentAIPackage.done();
    }

    belay = () => {
        window.dispatchEvent(new Event('show-manual-controls'));
    };

    concur = () => {
        this.doTheNextThing();
    };

    autoEngaged = () => {
        window.dispatchEvent(new Event('hide-manual-controls'));
    }

}
